import moment from 'moment';
import Globalize from 'globalize';
import i18next from 'i18next';
import formatNumber from 'utils/NumberFormatter';
import NumberFormats from 'utils/personalizer/NumberFormats';

if (typeof i18next.loadNamespaces === 'function') {
    i18next.loadNamespaces(['units']);
}

function DateFormatter() {}

DateFormatter.LINK_FORMAT = 'YYYY-MM-DD';

// These are the formats used to format dates.
DateFormatter.DateFormats = {
    MMDDYYYY: 'mmddyyyy',
    DDMMYYYY: 'ddmmyyyy',
    YYYYMMDD: 'yyyymmdd',
    MMDD: 'mmdd'
};

DateFormatter.TimeFormats = {
    HOUR12: 'time_twelve_hr',
    HOUR24: 'time_twenty_four_hr'
};

var currentDateFormat = DateFormatter.DateFormats.MMDDYYYY;
var currentTimeFormat = null;

var mediumDateFormatMap = {};
mediumDateFormatMap[DateFormatter.DateFormats.MMDDYYYY] = 'MMMM d, yyyy';
mediumDateFormatMap[DateFormatter.DateFormats.DDMMYYYY] = 'd MMMM yyyy';
mediumDateFormatMap[DateFormatter.DateFormats.YYYYMMDD] = 'yyyy MMMM d';

DateFormatter.isTimeFormatHOUR12 = function() {
    return currentTimeFormat === DateFormatter.TimeFormats.HOUR12;
};

DateFormatter.isTimeFormatHOUR24 = function() {
    return currentTimeFormat === DateFormatter.TimeFormats.HOUR24;
};

DateFormatter.setDateFormat = function(dateFormat) {
    currentDateFormat = dateFormat;
};

DateFormatter.setTimeFormat = function(timeFormat) {
    currentTimeFormat = timeFormat;
};

/**
 * Formats the given date object as a String so that it appears in a simple format of MM/DD/YYYY or MM/DD/YY. However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 *
 * Ex. 11/23/2012
 * Ex. 11/23/12
 *
 * @param dateObj the value to format.
 * @param {boolean} shortYear - optional : if true, use 2-digit year
 */
DateFormatter.formatDate = function(dateObj, shortYear = false) {
    var year = shortYear
        ? dateObj
              .getFullYear()
              .toString()
              .substr(2, 2)
        : dateObj.getFullYear().toString();
    var mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    var dd = dateObj.getDate().toString();

    switch (currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            return (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd) + '/' + year;
        case this.DateFormats.DDMMYYYY:
            return (dd.length > 1 ? dd : '0' + dd) + '/' + (mm.length > 1 ? mm : '0' + mm) + '/' + year;
        case this.DateFormats.YYYYMMDD:
            return year + '/' + (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd);
        default:
            return (mm.length > 1 ? mm : '0' + mm) + '/' + (dd.length > 1 ? dd : '0' + dd) + '/' + year;
    }
};

/**
 * @param {string} dateString
 * @returns {string} formatted date string
 *
 * @example
 * DateFormatter.formatDateString('2019-02-01') => 02/01/2019 if currentDateFormat is MMDDYYYY
 * DateFormatter.formatDateString('2019-02-01') => 01/02/2019 if currentDateFormat is DDMMYYYY
 * DateFormatter.formatDateString('2019-02-01') => 2019/02/01 if currentDateFormat is YYYYMMDD
 * DateFormatter.formatDateString('2019-02-01') => 2/1 if currentDateFormat is MMDD
 *
 */
DateFormatter.formatDateString = function(dateString, format) {
    const date = dateString.split('-');
    const year = date[0];
    const month = date[1];
    const day = date[2];

    switch (format || currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            return month + '/' + day + '/' + year;
        case this.DateFormats.DDMMYYYY:
            return day + '/' + month + '/' + year;
        case this.DateFormats.YYYYMMDD:
            return year + '/' + month + '/' + day;
        case this.DateFormats.MMDD:
            return `${+month}/${+day}`;
        default:
            return month + '/' + day + '/' + year;
    }
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day, Year. However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules. Also, if the
 * abbreviatedMonth flag is set to true, the month will be abbreviated.
 * Ex. Jan 3, 1971
 * Ex. If abbreviatedMonth is false, then January 3, 1971
 * @param dateObj the value to format.
 * @param abbreviatedMonth - optional : if true, use abbreviated month
 */
DateFormatter.formatMonthDayYear = function(dateObj, abbreviatedMonth = true) {
    var yyyy = dateObj.getFullYear().toString();
    var mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    var dd = formatAsiaDate(dateObj) || dateObj.getDate().toString();

    var mm2 = mm.length > 1 ? mm : '0' + mm;
    var month = i18next.t(`common:month.${abbreviatedMonth ? 'abbr.' : ''}` + mm2);

    switch (currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            return month + ' ' + dd + ', ' + yyyy;
        case this.DateFormats.DDMMYYYY:
            return dd + ' ' + month + ' ' + yyyy;
        case this.DateFormats.YYYYMMDD:
            return yyyy + ' ' + month + ' ' + dd;
        default:
            return month + ' ' + dd + ', ' + yyyy;
    }
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day. However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 *
 * Ex. Jan 3
 *
 * @param {Date} dateObj the value to format.
 * @param {boolean} [fullMonth] - optional
 */
DateFormatter.formatMonthDay = function(dateObj, fullMonth = false) {
    var mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    var dd = formatAsiaDate(dateObj) || dateObj.getDate().toString();

    var mm2 = mm.length > 1 ? mm : '0' + mm;
    var month = i18next.t(`common:month.${fullMonth ? '' : 'abbr.'}` + mm2);

    switch (currentDateFormat) {
        case this.DateFormats.DDMMYYYY:
            return dd + ' ' + month;
        case this.DateFormats.MMDDYYYY:
        case this.DateFormats.YYYYMMDD:
        default:
            return month + ' ' + dd;
    }
};

/**
 * Formats the given date object as a String so that it appears in format of Month only (using our translations).
 *
 * Ex. Jan
 * Ex. January, if fullMonth is true
 *
 * @param {Date} dateObj the value to format.
 * @param {boolean} fullMonth - optional
 */
DateFormatter.formatMonth = function(dateObj, fullMonth) {
    const mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    const mm2 = mm.length > 1 ? mm : '0' + mm;
    const monthId = fullMonth ? 'month.' : 'month.abbr.';
    const month = i18next.t('common:' + monthId + mm2);
    return month;
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day (using our translations). However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 *
 * Ex. Jan 3
 * Ex. January 3, if fullMonth is true
 *
 * @param {Date} dateObj the value to format.
 * @param fullMonth - optional
 */
DateFormatter.formatMonthDayLocalizer = function(dateObj, fullMonth) {
    const mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    const dd = formatAsiaDate(dateObj) || dateObj.getDate().toString();
    const mm2 = mm.length > 1 ? mm : '0' + mm;
    const monthId = fullMonth ? 'month.' : 'month.abbr.';
    const month = i18next.t('common:' + monthId + mm2);

    switch (currentDateFormat) {
        case this.DateFormats.DDMMYYYY:
            return dd + ' ' + month;
        default:
            return month + ' ' + dd;
    }
};

/**
 * Formats the given date object as a String with month and year only
 *
 * Ex. Jan 2018
 *
 * @param dateObj the value to format.
 */
DateFormatter.formatMonthYear = function(dateObj) {
    var mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    var yyyy = dateObj.getFullYear().toString();

    var mm2 = mm.length > 1 ? mm : '0' + mm;
    var month = i18next.t('common:month.abbr.' + mm2);

    switch (currentDateFormat) {
        case this.DateFormats.YYYYMMDD:
            return yyyy + ' ' + month;
        default:
            return month + ' ' + yyyy;
    }
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day. However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 * If the date in the date object is older than 1 year, return Mon Day, Year
 *
 * Ex. Jan 3 or
 * Ex. Jan 3, 1971
 *
 * @param dateObj the value to format.
 */
DateFormatter.formatMonthDayOptionalYear = function(dateObj) {
    const currentYear = moment().year();
    if (moment(dateObj).isBefore(`${currentYear}-01-01`, 'day')) {
        return DateFormatter.formatMonthDayYear(dateObj);
    }

    return DateFormatter.formatMonthDay(dateObj);
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day. However, if the
 * DateFormat is set to DD/MM/YYYY or YYYY/MM/DD it will format the date according to those rules.
 * If the year in the date from the date object is older than current year, return Mon Day, Year
 *
 * Ex. January 3 or Jan 3 or
 * Ex. Jan 3, 1971
 *
 * @param dateObj the value to format.
 * @param longMonth the boolean value for displaying the month in a short or long format
 */
DateFormatter.formatMonthDayLocalizerOptionalYear = function(dateObj, longMonth = true) {
    if (moment(dateObj).year() < moment().year()) {
        return DateFormatter.formatMonthDayYear(dateObj);
    }

    return DateFormatter.formatMonthDayLocalizer(dateObj, longMonth);
};

/**
 * Formats the given date range as a String following the date range format from date and time formatting guidelines.
 * It will format the date range based on the current date format.
 *
 * Ex.
 * Days in same month, current year: Jul 24-31
 * Days in same month, prior year: Oct 25-29, 2013
 * Days across months, current year: Jun 26 - Jul 2
 * Days across months, prior year: Jun 26 - Jul 2, 2020
 * Days across years: Dec 19, 2013 - Jan 5, 2014
 *
 * @param startDate the beginning date from the date range.
 * @param endDate the ending date from the date range.
 */
DateFormatter.formatDateRange = function(startDate, endDate) {
    const isSameYear = startDate.getFullYear() === endDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const start = isSameYear ? DateFormatter.formatMonthDay(startDate) : DateFormatter.formatMonthDayYear(startDate);
    const end = isSameYear ? DateFormatter.formatMonthDay(endDate) : DateFormatter.formatMonthDayYear(endDate);
    const dateSeparator = ' - ';
    const dateSeparatorNumbers = '-';

    switch (currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            if (isSameYear && startDate.getFullYear() !== currentYear) {
                if (startDate.getMonth() === endDate.getMonth()) {
                    return start + dateSeparatorNumbers + end.split(' ')[1] + ', ' + startDate.getFullYear();
                } else {
                    return start + dateSeparator + end + ', ' + startDate.getFullYear();
                }
            }

            if (isSameYear && startDate.getMonth() === endDate.getMonth()) {
                return start + dateSeparatorNumbers + end.split(' ')[1];
            } else {
                return start + dateSeparator + end;
            }
        case this.DateFormats.DDMMYYYY:
            if (isSameYear && startDate.getFullYear() !== currentYear) {
                if (startDate.getMonth() === endDate.getMonth()) {
                    return start.split(' ')[0] + dateSeparatorNumbers + end + ' ' + startDate.getFullYear();
                } else {
                    return start + dateSeparator + end + ' ' + startDate.getFullYear();
                }
            }

            if (isSameYear && startDate.getMonth() === endDate.getMonth()) {
                return start.split(' ')[0] + dateSeparatorNumbers + end;
            } else {
                return start + dateSeparator + end;
            }
        case this.DateFormats.YYYYMMDD:
            if (isSameYear && startDate.getFullYear() !== currentYear) {
                if (startDate.getMonth() === endDate.getMonth()) {
                    return startDate.getFullYear() + ' ' + start + dateSeparatorNumbers + end.split(' ')[1];
                } else {
                    return startDate.getFullYear() + ' ' + start + dateSeparator + end;
                }
            }

            if (isSameYear && startDate.getMonth() === endDate.getMonth()) {
                return start + dateSeparatorNumbers + end.split(' ')[1];
            } else {
                return start + dateSeparator + end;
            }
    }
};

/**
 * Formats the given date object as a String so that it appears in format of Mon Day. However, if the
 * DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 * If the date in the date object is in the last calendar year, return Mon Day, Year
 *
 * Ex. Jan 3 or
 * Ex. Jan 3, 2019 (if current year is greater than the passed-in date)
 *
 * @param {Date} dateObj the value to format.
 * @param {boolean} [fullMonth]
 */
DateFormatter.formatMonthDayOptionalCalendarYear = function(dateObj, fullMonth = false) {
    const dateYear = dateObj.getFullYear();
    const currentYear = new Date().getFullYear();

    if (dateYear !== currentYear) {
        return DateFormatter.formatMonthDayYear(dateObj);
    }

    return DateFormatter.formatMonthDay(dateObj, fullMonth);
};

/**
 * Formats the given date object as a String so that it appears in format of Month Day, Year with the month spelled
 * out entirely. However, if the DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 *
 * Ex. January 3, 1971
 *
 * @param dateObj the value to format.
 */
DateFormatter.formatLongMonthDayYear = function(dateObj) {
    return DateFormatter.formatMonthDayYear(dateObj, false);
};

/**
 * Same as `DateFormatter.formatLongMonthDayYear`, with alternate lib `Globalize`.
 *
 * Ex. January 3, 1971
 * @deprecated
 * @param dateObj the value to format.
 */
DateFormatter.formatLongMonthDayYearGlobalize = function(dateObj) {
    return Globalize.format(dateObj, mediumDateFormatMap[currentDateFormat]);
};

/**
 * Formats the given date object as a String so that it appears in format of as MMM DD, YYYY @ HH:MM.
 *
 * Ex. Dec 23, 1981 @ 11:23 AM
 *
 * @param dateObj the value to format.
 */
DateFormatter.formatDateAndTime = function(dateObj) {
    var yyyy = dateObj.getFullYear().toString();
    var mm = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
    var dd = formatAsiaDate(dateObj) || dateObj.getDate().toString();

    var hh = dateObj.getHours().toString();
    var mi = dateObj.getMinutes().toString();

    var h = hh;
    var suffix = '';
    if (currentTimeFormat == 'time_twenty_four_hr') {
        //Move along.  There is nothing to see here.
        //If the user has a 24-Hour time format defined we want to bypass the format of the time suffix
        //The default should be in AM/PM if the preference is not defined.
    } else {
        suffix = i18next.t('common:AM');
        if (h >= 12) {
            h = hh - 12;
            suffix = i18next.t('common:PM');
        }
        if (h == 0) {
            h = 12;
        }
    }

    var todayDate = new Date();
    var todayYYYY = todayDate.getFullYear().toString();
    var todayMM = (todayDate.getMonth() + 1).toString();
    var todayDD = todayDate.getDate().toString();

    var compareDate1 = yyyy + mm + dd;
    var compareDate2 = todayYYYY + todayMM + todayDD;

    //If the date is equal to today, Return the literal and time of day
    if (compareDate1 == compareDate2) {
        var formatTimeToday = i18next.t('common:today') + ' @ ' + h + ':' + (mi.length > 1 ? mi : '0' + mi);

        if (suffix) {
            formatTimeToday += ' ' + suffix;
        }

        return formatTimeToday;
    }

    var mm2 = mm.length > 1 ? mm : '0' + mm;
    var month = i18next.t('common:month.abbr.' + mm2);

    var formatTime = ' @ ' + h + ':' + (mi.length > 1 ? mi : '0' + mi);

    if (suffix) {
        formatTime += ' ' + suffix;
    }

    switch (currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            return month + ' ' + dd + ', ' + yyyy + formatTime;
        case this.DateFormats.DDMMYYYY:
            return dd + ' ' + month + ' ' + yyyy + formatTime;
        case this.DateFormats.YYYYMMDD:
            return yyyy + ' ' + month + ' ' + dd + formatTime;
        default:
            return month + ' ' + dd + ', ' + yyyy + formatTime;
    }
};

/**
 * Formats the given time object so that it will intelligently display, just now, X minutes ago, X hours ago, yesterday,
 * X days ago.
 *
 * Document: https://confluence.garmin.com/pages/viewpage.action?pageId=653071524
 *
 * 1-59 seconds ago                            = Just now
 * 1-59 minutes ago                            = 35 mins ago
 * 1-12 hours ago                              = 9 hours ago
 * 13 or more hours ago (but current day)      = Today
 * Previous Day                                = Yesterday
 * Between 2 and 7 days ago                    = Tuesday
 * 7 or more days ago                          = Dec 25
 * 7 or more days ago, prior year              = Dec 25, 2013
 *
 * @param timeLocal the object to format.
 */
DateFormatter.formatTimeAgo = function(timeLocal) {
    // Add a member for number of days ago.
    let elapsedMilliseconds = new moment().toDate().getTime() - timeLocal.getTime(),
        agoValue = '',
        agoKey = '',
        formatted = false,
        now = new moment(),
        then = new moment(timeLocal),
        daysDateOnly = moment(now.format('YYYY-MM-DD')).diff(moment(then.format('YYYY-MM-DD')), 'days'),
        sign = function(x) {
            return (x > 0) - (x < 0) || +x;
        };

    if (daysDateOnly > 0) {
        if (daysDateOnly > 1) {
            if (daysDateOnly < 7) {
                agoValue = '';
                agoKey = 'day.' + timeLocal.getDay();
            } else {
                agoValue = '';
                formatted = true;
                agoKey = this.formatMonthDayOptionalCalendarYear(timeLocal);
            }
        } else {
            agoValue = '';
            agoKey = 'yesterday';
        }
    } else if (sign(elapsedMilliseconds) === -1) {
        // timeLocal is future date
        agoValue = '';
        formatted = true;
        agoKey = this.formatMonthDayOptionalCalendarYear(timeLocal);
    } else if (elapsedMilliseconds < 3600000) {
        agoValue = Math.floor(elapsedMilliseconds / 60000);
        if (agoValue > 1) {
            agoKey = 'mins_ago';
        } else if (agoValue <= 0) {
            agoValue = '';
            agoKey = 'just_now';
        } else {
            agoKey = 'min_ago';
        }
    } else if (elapsedMilliseconds < 86400000) {
        agoValue = Math.floor(elapsedMilliseconds / 3600000);
        let diffDays = now.diff(then, 'days');
        if (agoValue > 1) {
            if (agoValue >= 13 && diffDays === 0) {
                agoValue = '';
                agoKey = 'today';
            } else {
                agoKey = 'hours_ago';
            }
        } else {
            agoKey = 'hour_ago';
        }
    }

    return formatted ? agoKey : i18next.t('common:' + agoKey, { 0: agoValue });
};

/**
 * Get the days diff between two dates. For a positive result, firstDate should be ahead of secondDate
 *
 * @param firstDate first date object to diff with
 * @param secondDate second date object to diff with
 */
DateFormatter.getDaysDiffBetweenTwoDates = function(firstDate, secondDate) {
    // Compare two dates at start of day, i.e., 00:00
    const startOfFirstDate = moment(firstDate).startOf('day');
    const startOfSecondDate = moment(secondDate).startOf('day');
    return startOfSecondDate.diff(startOfFirstDate, 'days');
};

/**
 * Get the days diff between today and target date (timeLocal)
 *
 * @param timeLocal target date object to diff with today
 */
DateFormatter.getDaysDiff = function(timeLocal) {
    // Compare two dates at start of day, i.e., 00:00
    let startOfToday = new moment().startOf('day');
    let startOfTimeLocal = new moment(timeLocal).startOf('day');
    return startOfToday.diff(startOfTimeLocal, 'days');
};

/**
 * Get the hours diff between now and target local time (timeLocal)
 *
 * @param timeLocal target date object to diff with today
 */
DateFormatter.getHoursDiff = function(timeLocal) {
    let momentNow = new moment();
    let momentTimeLocal = new moment(timeLocal);
    return momentNow.diff(momentTimeLocal, 'hours');
};

/**
 * Formats the given date object as a date String, but will substitute human words for any date within the last week.
 * The format will be the same as formatLongMonthDayYear, except that it will substitute the words Today, Yesterday, Day of Week
 * when appropriate.
 *
 * Ex. Today
 *     Yesterday
 *     Tuesday
 *     December 25, 2021 (shortDate = false)
 *     Dec 25            (shortDate = true, and timeLocal is in the same year)
 *     Dec 25, 2020      (shortDate = true, and timeLocal is in prior year)
 *
 * @param timeLocal date object to format.
 * @param shortDate If true, displays a short date instead, the same way that is formatted by formatMonthDayOptionalCalendarYear().
 */
DateFormatter.formatFriendlyDate = function(timeLocal, shortDate) {
    // Add a member for number of days ago.
    const agoValue = DateFormatter.getDaysDiff(timeLocal);
    let agoKey = '';

    function displayRegDate(value) {
        if (value < 7) {
            // Display day of week
            agoKey = 'day.' + timeLocal.getDay();
            return i18next.t('common:' + agoKey);
        } else {
            // Display full date
            if (shortDate) {
                return DateFormatter.formatMonthDayOptionalCalendarYear(timeLocal);
            } else {
                return DateFormatter.formatLongMonthDayYear(timeLocal);
            }
        }
    }

    // Future
    if (agoValue < 0) {
        // -1 represents a date after today's date
        const future = agoValue * -1;
        if (future === 1) {
            // Tomorrow
            agoKey = 'tomorrow';
        } else {
            return displayRegDate(future);
        }
    }

    // Today
    else if (agoValue === 0) {
        agoKey = 'today';
    }

    // Yesterday
    else if (agoValue === 1) {
        agoKey = 'yesterday';
    }

    // Ago
    else {
        return displayRegDate(agoValue);
    }

    return i18next.t('common:' + agoKey);
};

/**
 * Formats the given date object as a String, but will substitute human words for any date within the last week so
 * that it appears in format of as MMM DD, YYYY @ HH:MM.
 *
 * Ex. Dec 23, 1981 @ 11:23 AM
 *     Today @ 12:55 PM
 *
 * @param dateObj the value to format.
 */
DateFormatter.formatFriendlyDateAndTime = function(dateObj, shortDate) {
    var friendlyDate = DateFormatter.formatFriendlyDate(dateObj, shortDate);
    var formatTime = DateFormatter.formatLocalizeTime(dateObj);
    return friendlyDate + ' @ ' + formatTime;
};

/**
 * Formats the time of the given date object in the format of HH:MM XX or HH XX.
 *
 * Ex. 12:04 PM
 * Ex. 16:02 (if 24 hour formatting).
 *
 */
DateFormatter.formatTime = function(dateObj, hoursOnly, twentyFourHourFormat = false) {
    var time;
    var hour;
    var minutes;
    if (currentTimeFormat == DateFormatter.TimeFormats.HOUR24 || twentyFourHourFormat) {
        hour = formatNumber(dateObj.getHours(), NumberFormats.TWO_DIGITS);
        minutes = formatNumber(dateObj.getMinutes(), NumberFormats.TWO_DIGITS);
        time = hour + ':' + minutes;
    } else {
        var hour24 = dateObj.getHours();
        var amPm;

        if (hour24 == 0 || hour24 == 12) {
            hour = 12;
            amPm = hour24 == 0 ? i18next.t('common:AM') : i18next.t('common:PM');
        } else {
            hour = hour24 % 12;
            amPm = hour24 > 12 ? i18next.t('common:PM') : i18next.t('common:AM');
        }

        minutes = formatNumber(dateObj.getMinutes(), NumberFormats.TWO_DIGITS);

        if (hoursOnly) {
            time = hour + ' ' + amPm;
        } else {
            time = hour + ':' + minutes + ' ' + amPm;
        }
    }
    return time;
};

/**
 * Formats the time of the given date object in the format of HH:MM Localized(XX.
 *
 * Ex. 12:04 PM
 * Ex. 16:02 (if 24 hour formatting).
 *
 */
DateFormatter.formatLocalizeTime = function(dateObj) {
    var time;
    var hour;
    var minutes;
    if (currentTimeFormat == DateFormatter.TimeFormats.HOUR24) {
        hour = formatNumber(dateObj.getHours(), NumberFormats.TWO_DIGITS);
        minutes = formatNumber(dateObj.getMinutes(), NumberFormats.TWO_DIGITS);
        time = hour + ':' + minutes;
    } else {
        var hour24 = dateObj.getHours();
        var amPm;

        if (hour24 == 0 || hour24 == 12) {
            hour = 12;
            amPm = hour24 == 0 ? i18next.t('common:AM') : i18next.t('common:PM');
        } else {
            hour = hour24 % 12;
            amPm = hour24 > 12 ? i18next.t('common:PM') : i18next.t('common:AM');
        }

        minutes = formatNumber(dateObj.getMinutes(), NumberFormats.TWO_DIGITS);

        time = hour + ':' + minutes + ' ' + amPm;
    }
    return time;
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DD.  This is mostly used when
 * interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatISODate = function(dateObj) {
    var yyyy = dateObj.getFullYear();
    var mm = dateObj.getMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getDate();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DD.  This is mostly used when
 * interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatISODateUTC = function(dateObj) {
    var yyyy = dateObj.getUTCFullYear();
    var mm = dateObj.getUTCMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getUTCDate();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DDT00:00:00.000Z.  This is mostly used when
 * interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02T16:04:00.000Z
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatISODateLong = function(dateObj) {
    return new moment(dateObj).toISOString();
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DD HH:MM:SS.  This is mostly used when
 * interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02 14:13:02
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatMBTimestamp = function(dateObj) {
    var yyyy = dateObj.getFullYear();
    var mm = dateObj.getMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getDate();

    var h = dateObj.getHours();
    var m = dateObj.getMinutes();
    var s = dateObj.getSeconds();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS) +
        ' ' +
        formatNumber(h, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(m, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(s, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DD HH:MM:SS, but is displayed not in local time
 * but in UTC time.  This is mostly used when interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02 14:13:02
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatMBTimestampUTC = function(dateObj) {
    var yyyy = dateObj.getUTCFullYear();
    var mm = dateObj.getUTCMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getUTCDate();

    var h = dateObj.getUTCHours();
    var m = dateObj.getUTCMinutes();
    var s = dateObj.getUTCSeconds();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS) +
        ' ' +
        formatNumber(h, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(m, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(s, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DDTHH:MM:SS.00.  This is mostly used when
 * interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02T14:13:02T.00
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatConnectTimestamp = function(dateObj) {
    var yyyy = dateObj.getFullYear();
    var mm = dateObj.getMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getDate();

    var h = dateObj.getHours();
    var m = dateObj.getMinutes();
    var s = dateObj.getSeconds();
    var ms = dateObj.getMilliseconds();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS) +
        'T' +
        formatNumber(h, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(m, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(s, NumberFormats.TWO_DIGITS) +
        '.' +
        formatNumber(ms, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the given date object as a String in the format of YYYY-MM-DDTHH:MM:SS.00, but is displayed not in local time
 * but in UTC time.  This is mostly used when interacting with APIs and not normally presented to the user.
 *
 * Ex. 2014-12-02T14:13:02.00
 *
 * @param dateObj the object to format.
 * @returns {string}
 */
DateFormatter.formatConnectTimestampUTC = function(dateObj) {
    var yyyy = dateObj.getUTCFullYear();
    var mm = dateObj.getUTCMonth() + 1; // getMonth() is zero-based
    var dd = dateObj.getUTCDate();

    var h = dateObj.getUTCHours();
    var m = dateObj.getUTCMinutes();
    var s = dateObj.getUTCSeconds();
    var ms = dateObj.getUTCMilliseconds();

    return (
        formatNumber(yyyy, NumberFormats.FOUR_DIGITS) +
        '-' +
        formatNumber(mm, NumberFormats.TWO_DIGITS) +
        '-' +
        formatNumber(dd, NumberFormats.TWO_DIGITS) +
        'T' +
        formatNumber(h, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(m, NumberFormats.TWO_DIGITS) +
        ':' +
        formatNumber(s, NumberFormats.TWO_DIGITS) +
        '.' +
        formatNumber(ms, NumberFormats.TWO_DIGITS)
    );
};

/**
 * Formats the date given in ISO format to Day, Month D or Day, D Month depending on user preference
 *
 * Ex. Saturday, Dec 1 or Saturday, 1 Dec
 *
 * @param date: Ex. "2017-11-11T06:00:00.000Z" or "2017-11-11",
 * @param shortDayName {Boolean} - Ex output for "true": Sat, Dec 1 or Sat, 1 Dec
 */
DateFormatter.formatDayMonthDate = function(date, shortDayName) {
    var eventDayOfWeek = moment(date).format('d');
    var eventMonth = moment(date).format('MM');
    var eventDayOfMonth = moment(date).format('D');
    var dayKey = 'day.' + eventDayOfWeek;

    if (shortDayName === true) {
        dayKey += '.short';
    }

    switch (currentDateFormat) {
        case this.DateFormats.MMDDYYYY:
            return (
                i18next.t('common:' + dayKey) +
                ', ' +
                i18next.t('common:month.abbr.' + eventMonth) +
                ' ' +
                eventDayOfMonth
            );
        case this.DateFormats.DDMMYYYY:
            return (
                i18next.t('common:' + dayKey) +
                ', ' +
                eventDayOfMonth +
                ' ' +
                i18next.t('common:month.abbr.' + eventMonth)
            );
        default:
            return (
                i18next.t('common:' + dayKey) +
                ', ' +
                i18next.t('common:month.abbr.' + eventMonth) +
                ' ' +
                eventDayOfMonth
            );
    }
};

DateFormatter.personalizeDateRange = function(startDate, untilDate, displayPeriod) {
    var aDate = startDate.split('-');
    var startDate = new Date(aDate[0], aDate[1] - 1, aDate[2], 0, 0, 0);
    var bDate = untilDate.split('-');
    var endDate = new Date(bDate[0], bDate[1] - 1, bDate[2], 0, 0, 0);

    var formatDate = '';

    switch (displayPeriod) {
        case 'daily':
        case 'weekly':
            if (startDate.getFullYear() == endDate.getFullYear()) {
                //same year, only show on start or end date
                if (currentDateFormat == DateFormatter.DateFormats.YYYYMMDD) {
                    formatDate =
                        DateFormatter.formatMonthDayYear(startDate) + ' - ' + DateFormatter.formatMonthDay(endDate);
                } else {
                    formatDate =
                        DateFormatter.formatMonthDay(startDate) + ' - ' + DateFormatter.formatMonthDayYear(endDate);
                }
            } else {
                formatDate =
                    DateFormatter.formatMonthDayYear(startDate) + ' - ' + DateFormatter.formatMonthDayYear(endDate);
            }

            break;

        case 'monthly':
        case 'yearly':
            var yyyy = startDate.getFullYear().toString();
            var mm = (startDate.getMonth() + 1).toString();

            var endyyyy = endDate.getFullYear().toString();
            var endmm = (endDate.getMonth() + 1).toString();

            var formatBeginMonth = i18next.t('common:month.abbr.' + (mm.length > 1 ? mm : '0' + mm));
            var formatEndMonth = i18next.t('common:month.abbr.' + (endmm.length > 1 ? endmm : '0' + endmm));

            if (currentDateFormat == DateFormatter.DateFormats.YYYYMMDD) {
                formatDate =
                    yyyy +
                    ' ' +
                    formatBeginMonth +
                    (yyyy == endyyyy ? ' - ' + formatEndMonth : ' - ' + endyyyy + ' ' + formatEndMonth);
            } else {
                formatDate =
                    formatBeginMonth +
                    (yyyy == endyyyy ? ' - ' + formatEndMonth : ' ' + yyyy + ' - ' + formatEndMonth) +
                    ' ' +
                    endyyyy;
            }
            break;
    }

    return formatDate;
};

/**
 * Formats the given date object as a String.
 *
 * Ex. Tuesday, Nov 8 at 3:46 PM
 *
 * @param date the value to format.
 */
DateFormatter.formatFriendlyLongDateAndTime = function(date) {
    var d = moment(date).toDate();
    return (
        DateFormatter.formatDayMonthDate(d) + ' ' + i18next.t('common:at') + ' ' + DateFormatter.formatLocalizeTime(d)
    );
};

/**
 * Formats the given date object as a String.
 * Today will be spelled out if the date is today.
 * If the DateFormat is set to DD/MM/YYYY or YYYY/MM/DD it will format the date according to those rules.
 * If the DateFormat is set to DD/MM or MM/DD it will format the date according to those rules.
 *
 * Default format: DD/MM/YY at HH:MM
 * The default separator is: " at "
 * The separator can be modified by passing a string as the second parameter.
 *
 * Ex. 11/08/22 at 3:46 PM
 * Ex. Today at 12:00 PM
 *
 * With separator: ", "
 * Ex. 11/08/22, 3:46 PM
 * Ex. Today, 12:00 PM
 *
 * @param date the value to format.
 */
DateFormatter.formatShortDateAndTime = function(date, separator = ` ${i18next.t('common:at')} `) {
    var d = moment(date).toDate();
    const agoValue = DateFormatter.getDaysDiff(date);

    if (agoValue === 0) {
        return `${i18next.t('common:today')}${separator}${DateFormatter.formatLocalizeTime(d)}`;
    }

    return `${DateFormatter.formatDate(d, true)}${separator}${DateFormatter.formatLocalizeTime(d)}`;
};

/**
 * Formats the given date object as a String so that it appears in format of Day of week, Mon Day, Year.
 * Also has an option to provide a short version that abbreviates the day of the week and month name.
 * If the DateFormat is set to DD/MM/YYYY or or YYYY/MM/DD it will format the date according to those rules.
 * If Globalize is set to false, it will not use Globalize to localize the date year.
 *
 * Ex. Monday, January 3, 1971
 * Ex. Mon, Jan 3, 1971 (if short)
 *
 *
 * @param dateObj the value to format
 * @param short boolean
 * @param globalize boolean
 */
DateFormatter.formatDayOfWeekMonthDayYear = function(dateObj, short, globalize = true) {
    var dayOfWeek = moment(dateObj).format('d');
    var dayKey = 'day.' + dayOfWeek;

    if (short) {
        dayKey += '.short';

        return i18next.t('common:' + dayKey) + ', ' + DateFormatter.formatMonthDayYear(dateObj);
    } else if (globalize) {
        return i18next.t('common:' + dayKey) + ', ' + DateFormatter.formatLongMonthDayYear(dateObj);
    } else {
        return i18next.t('common:' + dayKey) + ', ' + DateFormatter.formatMonthDayYear(dateObj, false);
    }
};

/**
 * Formats the given date object as a date String, but will substitute human words for today, tomorrow and yesterday.
 * The format will be the same as formatDayMonthDate, except that it will substitute the words Today, Yesterday and Tomorrow
 * when appropriate.
 *
 * Ex. Today
 * Ex. Yesterday
 * Ex. Monday, Jan 3
 *
 * @param dateObj the value to format
 */

//Expect unit tests in CA-53174 once unit tests are fixed for all utilities in backbone
DateFormatter.formatFriendlyDayOfWeekMonthDay = function(timeLocal) {
    // Add a member for number of days ago.
    const agoValue = DateFormatter.getDaysDiff(timeLocal);
    let agoKey = '';

    if (agoValue < 0) {
        // -1 represents a date after today's date
        const future = agoValue * -1;
        if (future === 1) {
            // Tomorrow
            return i18next.t('common:tomorrow');
        } else {
            return DateFormatter.formatDayMonthDate(timeLocal);
        }
    }

    // Today
    if (agoValue === 0) {
        agoKey = 'today';
    }

    // Yesterday
    else if (agoValue === 1) {
        agoKey = 'yesterday';
    }

    // Ago
    else {
        return DateFormatter.formatDayMonthDate(timeLocal);
    }

    return i18next.t('common:' + agoKey);
};

/**
 * Returns the moment from the date string given based on the current date format the user has set.
 *
 * @param dateString the value to format given in one of the formats: MM/DD/YYYY, DD/MM/YYYY or YYYY/MM/DD
 */
DateFormatter.getMomentBasedOnDateFormat = function(dateString) {
    switch (currentDateFormat) {
        case 'mmddyyyy':
            return moment(dateString, 'MM/DD/YYYY');
        case 'ddmmyyyy':
            return moment(dateString, 'DD/MM/YYYY');
        case 'yyyymmdd':
            return moment(dateString, 'YYYY/MM/DD');
    }
};

/**
 * Get the diff object between the current time and the time provided
 * @param {Date} timeToDiff
 * @returns {{msDiff: number, minDiff: number, hourDiff: number, dayDiff: number}}
 */
const getTimeDiff = (timeToDiff) => {
    const msPerMin = 60000; // 1000 * 60
    const msPerHour = 3600000; // 1000 * 60 * 60

    const now = new Date();
    const msDiff = now.getTime() - timeToDiff.getTime();
    const minDiff = Math.floor(msDiff / msPerMin);
    const hourDiff = Math.floor(msDiff / msPerHour);
    // Calculate the diff between the two dates, the following function rely on moment which handle daylight saving time
    const dayDiff = Math.abs(DateFormatter.getDaysDiffBetweenTwoDates(timeToDiff, now));

    return {
        msDiff,
        minDiff,
        hourDiff,
        dayDiff
    };
};

/**
 * Document Reference: https://confluence.garmin.com/pages/viewpage.action?pageId=653071524
 *
 * Format Type: Relative Time
 * Features Where This Format Is Used:
 *   - News Feed
 *   - Group News Feed
 *   - Comments
 *   - Notification Center (Inbox)
 *
 * Format Guidelines
 *   - 1-59 seconds ago                   = Just now
 *   - 1-59 minutes ago                   = 35m
 *   - 1-24 hours ago                     = 9h
 *   - Previous Day                       = 1d
 *   - Between 2 and 7 days ago           = 4d
 *   - *7 or more days ago                = Dec 25
 *   - *7 or more days ago, prior year    = Dec 25, 2020
 *
 * Notes
 *   - All time differences will be calculated based on the viewer’s time zone.
 *   - *In the activity feed portions of the news feeds, dates will reflect the activity uploader’s time zone for the final two rules above (“7 or more days ago” and “7 or more days ago, prior year”)
 *
 * @param {Date} timeGMT the GMT time object to format.
 * @param {Date} [timeLocal] the local time object to format.
 */
DateFormatter.formatRelativeTime = function(timeGMT, timeLocal) {
    const { msDiff, minDiff, hourDiff, dayDiff } = getTimeDiff(timeGMT);

    let relativeTime = '';
    if (msDiff < 0) {
        /** future date */
        relativeTime = this.formatMonthDayOptionalCalendarYear(timeGMT);
    } else if (msDiff > 0 && minDiff === 0) {
        // 1-59 seconds ago: Just now
        relativeTime = i18next.t('common:just_now');
    } else if (minDiff > 0 && minDiff < 60) {
        // 1-59 minutes ago: 35m
        relativeTime = `${minDiff}${i18next.t('units:duration_minute_short')}`;
    } else if (hourDiff > 0 && hourDiff < 24) {
        // 1-24 hours ago: 9h
        relativeTime = `${hourDiff}${i18next.t('units:duration_hour_short')}`;
    } else if (dayDiff > 0 && dayDiff < 7) {
        // Previous Day:                    1d
        // Between 2 and 7 days ago:        4d
        relativeTime = `${dayDiff}${i18next.t('units:duration_day_short')}`;
    } else {
        // 7 or more days ago:              Dec 25
        // 7 or more days ago, prior year:  Dec 25, 2020
        relativeTime = timeLocal
            ? this.formatMonthDayOptionalCalendarYear(timeLocal)
            : this.formatMonthDayOptionalCalendarYear(timeGMT);
    }

    return relativeTime;
};

/**
 * Document Reference: https://confluence.garmin.com/pages/viewpage.action?pageId=653071524
 *
 * Format Type: Relative Time - Last Sync
 * Features Where This Format Is Used:
 *   - Leaderboards
 *   - User's Last Sync
 *
 * Format Guidelines
 *   - 1-59 seconds ago                   = Just now
 *   - 1-59 minutes ago                   = 35m ago
 *   - 1-24 hours ago                     = 9h ago
 *   - Previous Day                       = Yesterday
 *   - Between 2 and 7 days ago           = Tuesday
 *   - 7 or more days ago                 = Dec 25
 *   - 7 or more days ago, prior year     = Dec 25, 2020
 *
 * Notes
 *   - All time differences will be calculated based on the viewer’s time zone.
 *
 * @param {Date} timeLocal the local time object to format.
 */
DateFormatter.formatRelativeTimeLastSync = function(timeLocal) {
    const { msDiff, minDiff, hourDiff, dayDiff } = getTimeDiff(timeLocal);

    let relativeTime = '';
    if (msDiff < 0) {
        /** future date */
        relativeTime = this.formatMonthDayOptionalCalendarYear(timeLocal);
    } else if (msDiff > 0 && minDiff === 0) {
        // 1-59 seconds ago: Just now
        relativeTime = i18next.t('common:just_now');
    } else if (minDiff > 0 && minDiff < 60) {
        // 1-59 minutes ago: 35m ago
        relativeTime = i18next.t('common:m_ago', { 0: minDiff });
    } else if (hourDiff > 0 && hourDiff < 24) {
        // 1-24 hours ago: 9h ago
        relativeTime = i18next.t('common:h_ago', { 0: hourDiff });
    } else if (dayDiff === 1) {
        // Previous Day: Yesterday
        relativeTime = i18next.t('common:yesterday');
    } else if (dayDiff > 1 && dayDiff < 7) {
        // Between 2 and 7 days ago: Tuesday
        relativeTime = i18next.t(`common:day.${timeLocal.getDay()}`);
    } else {
        // 7 or more days ago:              Dec 25
        // 7 or more days ago, prior year:  Dec 25, 2020
        relativeTime = this.formatMonthDayOptionalCalendarYear(timeLocal);
    }

    return relativeTime;
};

/**
 * Returns day-of-month. Check need to format asia date if necessary
 * @param {Date} date
 */
DateFormatter.formatDay = function(date) {
    return formatAsiaDate(date) || date.getDate().toString();
};

/**
 * return day-of-week. ex for Monday
 * - `short`    = `Mon` (default)
 * - `long`     = `Monday`
 * - `abbr`     = `Mo`
 * @param {Date} date
 * @param {'short' | 'long' | 'abbr'} format
 * @returns
 */
DateFormatter.formatWeekday = function(date, format = 'short') {
    const messageKey = 'common:day.' + date.getDay();
    switch (format) {
        case 'long':
            return i18next.t(messageKey);
        case 'abbr':
            return i18next.t(messageKey + '.abbr');
        case 'short':
        default:
            return i18next.t(messageKey + '.short');
    }
};

const asiaDateLocales = ['zh_TW', 'zh_CN', 'ko', 'ja'];

/**
 * Format Asia langauges date with `Intl.DateTimeFormat` if necessary.
 *
 * Else return `null`
 *
 * @param {Date} date
 */
export function formatAsiaDate(date) {
    if (asiaDateLocales.includes(i18next.language)) {
        try {
            return Intl.DateTimeFormat(i18next.language.replace('_', '-'), { day: 'numeric' })
                .format(date)
                .trim();
        } catch (error) {
            return null;
        }
    }
    return null;
}

/**
 * Returns the UTC offset with the translated timezone
 * @param {Number} offset // offset in minutes Ex. -420
 * @param {String} timezone // timezone Ex. America/Los_Angeles
 *
 * @returns {String} // Ex. (UTC-07:00) Pacific Time
 */
export const formatUtcOffsetTimezone = (offset, timezone) => {
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.floor(Math.abs(offset) % 60);

    const offsetString = `(UTC${offset < 0 ? '-' : '+'}${hours <= 9 ? `0${hours}` : hours}:${
        minutes <= 9 ? `0${minutes}` : minutes
    })`;

    if (timezone === 'UTC' || timezone === 'utc') {
        return i18next.t('common:utc');
    } else if (timezone === 'gmt') {
        return i18next.t('common:gmt');
    } else if (timezone === 'Etc/GMT+12') {
        return i18next.t('common:Etc/GMT+12');
    } else {
        return i18next.t(`common:${timezone}_utc`, { 0: offsetString });
    }
};

export default DateFormatter;
